import { useEffect, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./ParentTelegramPage.css";

// 학원 관리 > 텔레그램 · 출결(2026-09-30). 학부모 텔레그램(@gindaily_bot) 연결 현황, 단체 메시지, 출결 화면.
// 알림 방식(알림톡/텔레그램 모드)과 텔레그램 문구는 학원 관리 > 알림 관리(NotificationModePage)에 있다.
// 이은 집은 알림톡 대신 텔레그램으로 받는다. 텔레그램 문구는 카카오 심사가 없어 여기서 바로 고친다.
// 서버: api/echo/parent_telegram.py, 출결: api/echo/attendance_views.py

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    window.prompt("아래 글을 복사해 주세요", text);
  }
};

export const fillPreview = (text, variableList) => {
  let result = text;
  variableList.forEach((one) => {
    result = result.split(`{${one.key}}`).join(one.example);
  });
  return result;
};

export const VariableChips = ({ variableList, onInsert }) => (
  <div className="parent-tg__chips">
    {variableList.map((one) => (
      <button key={one.key} type="button" title={`예: ${one.example}`} onClick={() => onInsert(`{${one.key}}`)}>
        {`{${one.key}}`} <span>{one.label}</span>
      </button>
    ))}
  </div>
);

// 커서 자리에 {학생명} 같은 자리를 끼워 넣는다
export const insertAtCursor = (textarea, value, current) => {
  if (!textarea) return current + value;
  const start = textarea.selectionStart ?? current.length;
  const end = textarea.selectionEnd ?? current.length;
  return current.slice(0, start) + value + current.slice(end);
};

export const TemplateCard = ({ template, variableList, onChange }) => {
  const textareaRef = useRef(null);
  return (
    <div className={`parent-tg__template${template.is_enabled ? "" : " parent-tg__template--off"}`}>
      <div className="parent-tg__template-head">
        <b>{template.label}</b>
        <label className="parent-tg__switch">
          <input type="checkbox" checked={template.is_enabled} onChange={(event) => onChange({ is_enabled: event.target.checked })} />
          {template.is_enabled ? "보내요" : "안 보내요"}
        </label>
      </div>
      <div className="parent-tg__template-body">
        <div>
          <textarea
            ref={textareaRef}
            rows={4}
            value={template.text}
            onChange={(event) => onChange({ text: event.target.value })}
          />
          <VariableChips
            variableList={variableList}
            onInsert={(value) => onChange({ text: insertAtCursor(textareaRef.current, value, template.text) })}
          />
          <div className="parent-tg__resets">
            {template.text !== template.default && (
              <button type="button" className="parent-tg__reset" onClick={() => onChange({ text: template.default })}>
                쓰던 알림톡 문구로
              </button>
            )}
            {template.short && template.text !== template.short && (
              <button type="button" className="parent-tg__reset" onClick={() => onChange({ text: template.short })}>
                짧은 텔레그램 문구로
              </button>
            )}
          </div>
        </div>
        <div className="parent-tg__phone">
          <span>미리보기</span>
          <div className="parent-tg__bubble">{fillPreview(`[{학원명}]\n${template.text}`, variableList)}</div>
        </div>
      </div>
    </div>
  );
};

const ParentTelegramPage = () => {
  const [data, setData] = useState(null);
  const [studentFilter, setStudentFilter] = useState("off");
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [copied, setCopied] = useState("");
  const broadcastRef = useRef(null);

  const apply = (next) => setData(next);

  useEffect(() => {
    apiClient.get("/api/v1/echo/teacher/parent-telegram", { withAuth: true }).then((response) => apply(response.data));
  }, []);

  if (!data) return null;

  const studentList = data.student_list || [];
  const connectedList = studentList.filter((one) => one.connected);
  const waitingList = studentList.filter((one) => !one.connected);
  const shownList = studentFilter === "on" ? connectedList : waitingList;
  const variableList = data.variable_list || [];
  const broadcastVariableList = variableList.filter((one) => one.key === "학생명" || one.key === "학원명");

  const copy = async (id, text) => {
    await copyText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const broadcast = async () => {
    setBroadcastMessage("");
    try {
      const response = await apiClient.post("/api/v1/echo/teacher/parent-telegram", { text: broadcastText }, { withAuth: true });
      setBroadcastMessage(`텔레그램을 연결한 ${response.data.sent_count}곳에 보내고 있어요.`);
      setBroadcastText("");
    } catch (caught) {
      setBroadcastMessage(caught.response?.data?.error || "보내지 못했어요.");
    }
  };

  const inviteText =
    "[학부모님께] 아이 학습 소식을 이제 텔레그램으로 받아 보실 수 있어요 📲\n" +
    "알림톡으로 받으신 아이 학습 페이지 위의 '텔레그램으로 받기' 를 눌러 동의하고 시작만 누르면 끝이에요.";

  return (
    <div className="parent-tg">
      <div className="parent-tg__head">
        <div>
          <div className="section-title">텔레그램</div>
          <p className="parent-tg__lead">
            학부모가 텔레그램(@{data.bot_username})을 연결하면 <b>알림톡 대신 텔레그램</b>으로 받아요. 문구는 심사 없이 바로 고칠 수 있어요.
          </p>
        </div>
        <button type="button" className="parent-tg__kiosk" onClick={() => window.open("/attendance", "_blank")}>
          🏫 출결 화면 열기
        </button>
      </div>
      {!data.enabled && <p className="parent-tg__warn">학부모 텔레그램이 아직 준비 중이에요.</p>}

      <p className="parent-tg__help">
        지금 알림 방식: <b>{data.channel === "telegram" ? "📲 텔레그램 모드" : "💬 알림톡 모드"}</b> · 방식과 문구는 학원 관리 &gt; 알림 관리에서 바꿔요.
      </p>

      <section className="parent-tg__card">
        <div className="parent-tg__card-title">
          학부모 연결 <em>{connectedList.length} / {studentList.length}명</em>
        </div>
        <div className="parent-tg__tabs">
          <button type="button" aria-pressed={studentFilter === "off"} onClick={() => setStudentFilter("off")}>
            아직 안 한 집 {waitingList.length}
          </button>
          <button type="button" aria-pressed={studentFilter === "on"} onClick={() => setStudentFilter("on")}>
            연결한 집 {connectedList.length}
          </button>
        </div>
        {studentFilter === "off" && waitingList.length > 0 && (
          <div className="parent-tg__bulk">
            <button type="button" onClick={() => copy("phones", waitingList.map((one) => one.parent_phone).filter(Boolean).join("\n"))}>
              {copied === "phones" ? "복사했어요" : "번호 전체 복사 (단체문자용)"}
            </button>
            <button type="button" onClick={() => copy("invite", inviteText)}>
              {copied === "invite" ? "복사했어요" : "안내 문자 문구 복사"}
            </button>
          </div>
        )}
        <div className="parent-tg__list">
          {shownList.map((one) => (
            <div key={one.id} className="parent-tg__row">
              <b>{one.name}</b>
              <span>{one.parent_phone}</span>
              {one.connected ? (
                <em className="parent-tg__on">✅ 연결함</em>
              ) : (
                <button type="button" onClick={() => copy(`link-${one.id}`, one.consent_url)}>
                  {copied === `link-${one.id}` ? "복사했어요" : "연결 링크 복사"}
                </button>
              )}
            </div>
          ))}
          {shownList.length === 0 && <p className="parent-tg__help">해당하는 학생이 없어요.</p>}
        </div>
      </section>

      <section className="parent-tg__card">
        <div className="parent-tg__card-title">단체 메시지 <em>연결한 {connectedList.length}곳</em></div>
        <p className="parent-tg__help">휴원 안내, 시험 일정처럼 모든 학부모께 보낼 글. 텔레그램을 연결한 집에만 가요. 광고는 보내지 마세요.</p>
        <textarea
          ref={broadcastRef}
          rows={4}
          value={broadcastText}
          placeholder={"예: {학생명} 학부모님, 10월 3일 개천절은 휴원합니다 🙏"}
          onChange={(event) => setBroadcastText(event.target.value)}
        />
        <VariableChips
          variableList={broadcastVariableList}
          onInsert={(value) => setBroadcastText((prev) => insertAtCursor(broadcastRef.current, value, prev))}
        />
        {broadcastText && (
          <div className="parent-tg__bubble parent-tg__bubble--inline">{fillPreview(`[{학원명}]\n${broadcastText}`, variableList)}</div>
        )}
        <button type="button" className="parent-tg__primary" disabled={!broadcastText.trim() || !connectedList.length} onClick={broadcast}>
          {connectedList.length}곳에 보내기
        </button>
        {broadcastMessage && <p className="parent-tg__message">{broadcastMessage}</p>}
      </section>

    </div>
  );
};

export default ParentTelegramPage;
